import { createTheme, svgIconClasses, Theme, ThemeProvider } from '@mui/material';
import { deepmerge } from '@mui/utils';
import { ChrThemeProvider } from '@chr/eds-react';
import { ReactNode } from 'react';
import { useTrackingMap } from './TrackingMapProvider';

export enum MapTypes {
  road = 'road',
  grayscale_dark = 'grayscale_dark',
  satellite = 'satellite',
}

declare module '@mui/material/styles' {
  interface Palette {
    map: {
      riskLevels: {
        early: string;
      };
      controls: {
        content: {
          primary: string;
          action: {
            active: {
              primary: string;
              secondary: string;
            };
          };
        };
        key: {
          active: string;
          hover: string;
        };
      };
      navigate: {
        primary: string;
      };
      disruptions: {
        primary: string;
        secondary: string;
        secondaryOpacity: number;
      };
      panel: {
        button: {
          hover: string;
        };
      };
      icons: {
        routeRemaining: {
          stroke: string;
        };
        affectedArea: {
          stroke: string;
        };
        routeTraveled: {
          stroke: string;
        };
        lastKnownLocation: {
          outerFill: string;
          middleFill: string;
          innerFill: string;
          stroke: string;
        };
        completedDestination: {
          fill: string;
        };
        plannedDestination: {
          fill: string;
          stroke: string;
        };
        plannedStop: {
          fill: string;
          stroke: string;
        };
        completedStop: {
          fill: string;
          inside: string;
        };
      };
      routes: {
        completed: string;
        notCompleted: string;
      };
    };
  }
}

export const MapThemeProvider = ({ children }: { children: ReactNode }) => {
  const { mapType } = useTrackingMap();
  const overrides = (theme: Theme) => {
    const isLightTheme = mapType === MapTypes.road;
    {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
    {/* @ts-ignore */}
    return createTheme(
      deepmerge(theme, {
        palette: {
          mode: isLightTheme ? 'light' : 'dark',
          ...(isLightTheme
            ? {
              alternate: {
                main: '#c1c1c2',
              },
              map: {
                riskLevels: {
                  early: '#8400ff',
                },
                controls: {
                  content: {
                    primary: '#003A6B',
                    action: {
                      active: {
                        primary: '#003A6B',
                        secondary: '#003A6B',
                      },
                      hover: {
                        primary: theme.palette.primary.dark,
                      },
                    },
                  },
                  key: {
                    active: '#cccccc',
                    hover: '#ebebeb',
                  },
                },
                navigate: {
                  primary: theme.palette.grey[700],
                },
                disruptions: {
                  primary: '#69370f',
                  secondary: '#FF8231',
                  secondaryOpacity: 0.25,
                },
                panel: {
                  button: {
                    hover: '#ebebeb',
                  },
                },
                icons: {
                  routeRemaining: {
                    stroke: '#0071C7',
                  },
                  affectedArea: {
                    stroke: '#69370F',
                  },
                  routeTraveled: {
                    stroke: '#0071C7',
                  },
                  lastKnownLocation: {
                    outerFill: '#0071C7',
                    middleFill: '#004986',
                    stroke: 'white',
                    innerFill: '#0071C7',
                  },
                  plannedDestination: {
                    fill: '#0071C7',
                  },
                  plannedStop: {
                    stroke: '#0071C7',
                    fill: 'white',
                  },
                  completedStop: {
                    inside: 'white',
                    fill: '#108DE0',
                  },
                  completedDestination: {
                    fill: '#0071C7',
                  },
                },
                routes: {
                  completed: '#0078ae',
                  notCompleted: '#0071C7',
                },
              },
            }
            : {
              background: {
                paper: theme.palette.grey[800],
              },
              text: {
                primary: theme.palette.typography.highContrast,
              },
              typography: {
                lowEmphasis: theme.palette.typography.highContrast,
              },
              primary: {
                main: theme.palette.primary.light,
              },
              alternate: {
                main: '#0f171e',
              },
              map: {
                riskLevels: {
                  early: '#8400ff',
                },
                controls: {
                  content: {
                    primary: '#003A6B',
                    action: {
                      active: {
                        primary: '#003A6B',
                        secondary: theme.palette.primary.light,
                      },
                      hover: {
                        primary: theme.palette.primary.light,
                      },
                    },
                  },
                  key: {
                    active: '#cccccc',
                    hover: '#ebebeb',
                  },
                },
                navigate: {
                  primary: theme.palette.grey[800],
                },
                disruptions: {
                  primary: '#f47b20',
                  secondary: 'rgba(244, 123, 32, 0.25)',
                },
                panel: {
                  button: {
                    hover: theme.palette.grey[900],
                  },
                },
                icons: {
                  routeRemaining: {
                    stroke: 'white',
                  },
                  affectedArea: {
                    stroke: '#F47B20',
                  },
                  routeTraveled: {
                    stroke: mapType === MapTypes.satellite ? 'white' : '#0071C7',
                  },
                  lastKnownLocation: {
                    outerFill: 'white',
                    middleFill: '#00496A',
                    stroke: '#EEEFEF',
                    innerFill: 'white',
                  },
                  plannedDestination: {
                    fill: '#004986',
                  },
                  plannedStop: {
                    stroke: 'white',
                    fill: '#004986',
                  },
                  completedStop: {
                    inside: mapType === MapTypes.satellite ? '#004986' : 'white',
                    fill: mapType === MapTypes.satellite ? 'white' : '#108DE0',
                  },
                  completedDestination: {
                    fill: mapType === MapTypes.satellite ? '#004986' : '#108DE0',
                  },
                },
                routes: {
                  completed: mapType === MapTypes.satellite ? '#FFFFFF' : '#108DE0',
                  notCompleted: '#FFFFFF',
                },
              },
            }),
        },
        components: {
          MuiTooltip: {
            styleOverrides: {
              tooltip: {
                fontSize: 11,
                backgroundColor: theme.palette.grey[800],
              },
              arrow: {
                color: theme.palette.grey[800],
              },
            },
          },
          MuiIcon: {
            styleOverrides: {
              root: {
                [`&.MuiIcon-fontSizeMedium, &.${svgIconClasses.fontSizeMedium}`]: {
                  fontSize: '24px',
                },
              },
            },
          },
        },
      }),
    );
  };

  return (
    <ChrThemeProvider>
      <ThemeProvider theme={overrides}>{children}</ThemeProvider>
    </ChrThemeProvider>
  );
};
